import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_THUMBNAIL_SLOT,
  PARTNER_THUMBNAIL_MAX_BYTES,
  UPLOAD_POLICIES,
  UPLOAD_PURPOSE_MARKETPLACE_PARTNER_THUMBNAIL,
  thumbnailSlotsForTargetRatios,
} from "@nexus/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@nexus/ui";

import { runPartnerThumbnailUpload } from "@/features/marketplace/api/partner-thumbnail-upload.api";
import { resolvePartnerPreflightContentType } from "@/features/marketplace/lib/resolve-partner-preflight-content-type";
import { getUserFacingApiMessage } from "@/lib/api-client";
import {
  useDeletePartnerOrderThumbnail,
  useUpsertPartnerOrderThumbnail,
} from "@/lib/queries/marketplace-partner.queries";

const THUMBNAIL_POLICY = UPLOAD_POLICIES[UPLOAD_PURPOSE_MARKETPLACE_PARTNER_THUMBNAIL];
const THUMBNAIL_ACCEPT = THUMBNAIL_POLICY.allowedContentTypes.join(",");

const RATIO_NAMES = {
  "16:9": ["marketplace.thumbnails.landscape", "Landscape"],
  "9:16": ["marketplace.thumbnails.vertical", "Vertical"],
  "4:5": ["marketplace.thumbnails.portrait", "Portrait"],
  "1:1": ["marketplace.thumbnails.square", "Square"],
} as const;

type ThumbnailView = { ratio: string; imageUrl: string | null };

export function OrderScriptThumbnails({
  orderId,
  orderScriptId,
  partnerTenantId,
  targetRatios,
  thumbnails,
  editable,
}: {
  orderId: string;
  orderScriptId: string;
  partnerTenantId?: string | null;
  targetRatios: readonly string[] | null | undefined;
  thumbnails: readonly ThumbnailView[] | undefined;
  editable: boolean;
}) {
  if (!editable) {
    return (
      <ThumbnailList
        targetRatios={targetRatios}
        thumbnails={thumbnails}
        editable={false}
      />
    );
  }
  return (
    <EditableThumbnails
      orderId={orderId}
      orderScriptId={orderScriptId}
      partnerTenantId={partnerTenantId ?? null}
      targetRatios={targetRatios}
      thumbnails={thumbnails}
    />
  );
}

function EditableThumbnails({
  orderId,
  orderScriptId,
  partnerTenantId,
  targetRatios,
  thumbnails,
}: {
  orderId: string;
  orderScriptId: string;
  partnerTenantId: string | null;
  targetRatios: readonly string[] | null | undefined;
  thumbnails: readonly ThumbnailView[] | undefined;
}) {
  const { t } = useTranslation();
  const upsert = useUpsertPartnerOrderThumbnail();
  const remove = useDeletePartnerOrderThumbnail();
  const [slotError, setSlotError] = useState<string | null>(null);
  const [pendingRatio, setPendingRatio] = useState<string | null>(null);

  const handleFile = async (ratio: string, file: File | undefined) => {
    if (!file || !partnerTenantId) return;
    const contentType = resolvePartnerPreflightContentType(file, THUMBNAIL_POLICY);
    if (!THUMBNAIL_POLICY.allowedContentTypes.includes(contentType)) {
      setSlotError(t("marketplace.thumbnails.rejectedType", "Use a JPG, PNG, or WebP image."));
      return;
    }
    if (file.size > PARTNER_THUMBNAIL_MAX_BYTES) {
      setSlotError(t("marketplace.thumbnails.rejectedSize", "Image must be 5 MB or smaller."));
      return;
    }
    setSlotError(null);
    setPendingRatio(ratio);
    try {
      const confirmed = await runPartnerThumbnailUpload({
        tenantId: partnerTenantId,
        file,
      });
      upsert.mutate(
        {
          partnerTenantId,
          orderId,
          orderScriptId,
          ratio,
          uploadId: confirmed.uploadId,
        },
        {
          onError: (error) => setSlotError(getUserFacingApiMessage(error)),
          onSettled: () => setPendingRatio(null),
        },
      );
    } catch (error) {
      setSlotError(getUserFacingApiMessage(error));
      setPendingRatio(null);
    }
  };

  return (
    <ThumbnailList
      targetRatios={targetRatios}
      thumbnails={thumbnails}
      editable
      pendingRatio={pendingRatio}
      errorMessage={slotError}
      onFile={handleFile}
      onRemove={(ratio) => {
        if (!partnerTenantId) return;
        setSlotError(null);
        setPendingRatio(ratio);
        remove.mutate(
          { partnerTenantId, orderId, orderScriptId, ratio },
          {
            onError: (error) => setSlotError(getUserFacingApiMessage(error)),
            onSettled: () => setPendingRatio(null),
          },
        );
      }}
    />
  );
}

function ThumbnailList({
  targetRatios,
  thumbnails,
  editable,
  pendingRatio,
  errorMessage,
  onFile,
  onRemove,
}: {
  targetRatios: readonly string[] | null | undefined;
  thumbnails: readonly ThumbnailView[] | undefined;
  editable: boolean;
  pendingRatio?: string | null;
  errorMessage?: string | null;
  onFile?: (ratio: string, file: File | undefined) => void;
  onRemove?: (ratio: string) => void;
}) {
  const { t } = useTranslation();
  const [enlarged, setEnlarged] = useState<ThumbnailView | null>(null);
  const slots = thumbnailSlotsForTargetRatios(targetRatios);
  const byRatio = new Map((thumbnails ?? []).map((row) => [row.ratio, row]));

  const labelFor = (slot: string) => {
    if (slot === DEFAULT_THUMBNAIL_SLOT) {
      return t("marketplace.thumbnails.default", "Default");
    }
    const namePair = RATIO_NAMES[slot as keyof typeof RATIO_NAMES];
    const name = namePair ? t(namePair[0], namePair[1]) : slot;
    return `${slot} ${name}`;
  };

  const enlargedLabel = enlarged ? labelFor(enlarged.ratio) : "";

  return (
    <div className="flex flex-col gap-2">
      <ul
        aria-label={t("marketplace.thumbnails.list", "Thumbnails")}
        className="flex flex-col gap-2"
      >
        {slots.map((slot) => {
          const stored = byRatio.get(slot);
          const imageUrl = stored?.imageUrl ?? null;
          const label = labelFor(slot);
          const pending = pendingRatio === slot;
          return (
            <li
              key={slot}
              className="flex flex-col gap-2 rounded-md bg-muted/40 px-3 py-2"
            >
              <div className="flex min-h-11 flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-medium text-foreground">{label}</span>
                {imageUrl ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setEnlarged({ ratio: slot, imageUrl })}
                  >
                    {t("marketplace.thumbnails.enlarge", "View larger thumbnail")}
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {t("marketplace.thumbnails.empty", "No thumbnail")}
                  </span>
                )}
              </div>
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={label}
                  className="h-24 w-full rounded-md object-contain bg-background"
                />
              ) : null}
              {editable ? (
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex">
                    <input
                      type="file"
                      accept={THUMBNAIL_ACCEPT}
                      className="sr-only"
                      aria-label={
                        imageUrl
                          ? t("marketplace.thumbnails.change", "Change thumbnail")
                          : t("marketplace.thumbnails.upload", "Upload thumbnail")
                      }
                      disabled={pending || pendingRatio != null}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        onFile?.(slot, file);
                      }}
                    />
                    <span className="inline-flex h-8 cursor-pointer items-center rounded-md border border-border px-3 text-xs font-medium text-foreground">
                      {imageUrl
                        ? t("marketplace.thumbnails.change", "Change thumbnail")
                        : t("marketplace.thumbnails.upload", "Upload thumbnail")}
                    </span>
                  </label>
                  {imageUrl ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={pending || pendingRatio != null}
                      onClick={() => onRemove?.(slot)}
                    >
                      {t("marketplace.thumbnails.remove", "Remove thumbnail")}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      {errorMessage ? (
        <p className="text-xs text-error" role="alert">
          {errorMessage}
        </p>
      ) : null}
      <Dialog
        open={enlarged != null}
        onOpenChange={(open) => {
          if (!open) setEnlarged(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{enlargedLabel}</DialogTitle>
          </DialogHeader>
          {enlarged?.imageUrl ? (
            <img
              src={enlarged.imageUrl}
              alt={enlargedLabel}
              className="max-h-[70vh] w-full object-contain"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
